import React from 'react';
import { lineTitle, lineSub, lineSize, backorderNote, money } from './poLine';
import { getOrgLocale } from '../../../lib/locale';
import { getCompanyProfile } from '../../../lib/companyProfile';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');

const printStyles = `
  @media print {
    @page { size: A4; margin: 12mm; }
    body * { visibility: hidden; }
    #printable-po, #printable-po * { visibility: visible; }
    #printable-po { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; }
    #printable-po tr { page-break-inside: avoid; }
    #printable-po { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
`;

const Box = ({ title, children, testid }) => (
  <div className="border border-slate-300 rounded-md overflow-hidden" data-testid={testid}>
    <div className="bg-slate-900 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5">{title}</div>
    <div className="px-3 py-2.5 text-[12px] leading-relaxed">{children}</div>
  </div>
);

const PrintPO = ({ po }) => {
  if (!po) return null;
  const co = getCompanyProfile() || {};
  const loc = getOrgLocale();
  const general = po.po_type === 'General';
  const showDims = !general && po.line_items.some((li) => li.width || li.length);
  const showTax = po.line_items.some((li) => Number(li.tax_rate) > 0);
  const cols = 5 + (showDims ? 1 : 0) + (showTax ? 1 : 0);

  return (
    <>
      <style>{printStyles}</style>
      <div id="printable-po" className="hidden print:block bg-white text-slate-900 p-8" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }} data-testid="printable-po">
        <div className="flex justify-between items-start gap-6 pb-4 border-b-4 border-slate-900">
          <div className="flex items-start gap-8 min-w-0">
            {co.logo && <img src={co.logo} alt="" className="max-h-20 max-w-[180px] object-contain pr-6 border-r border-slate-200" data-testid="po-print-logo" />}}
            <div className="text-[11px] leading-snug text-slate-700">
              <p className="text-[15px] font-bold text-slate-900">{co.name}</p>
              {co.abn && <p>ABN / Tax no.: {co.abn}</p>}
              {(co.addressLines || []).map((l) => <p key={l}>{l}</p>)}
              {(co.phone || co.email) && <p>{[co.phone, co.email].filter(Boolean).join('  ·  ')}</p>}
              {co.website && <p>{co.website}</p>}
            </div>
          </div>
          <div className="text-right shrink-0">
            <h1 className="text-[26px] font-extrabold tracking-wide leading-none">PURCHASE ORDER</h1>
            <table className="mt-3 ml-auto text-[12px]">
              <tbody>
                <tr><td className="pr-4 text-slate-500">PO number</td><td className="font-bold text-[14px]">{po.po_number}</td></tr>
                <tr><td className="pr-4 text-slate-500">Order date</td><td>{fmtDate(po.order_date)}</td></tr>
                <tr><td className="pr-4 text-slate-500">Delivery by</td><td className="font-semibold">{fmtDate(po.expected_delivery_date)}</td></tr>
                {po.supplier_reference && <tr><td className="pr-4 text-slate-500">Your ref.</td><td>{po.supplier_reference}</td></tr>}
                {po.job_number && <tr><td className="pr-4 text-slate-500">Job no.</td><td>{po.job_number}</td></tr>}
                <tr><td className="pr-4 text-slate-500">Currency</td><td>{po.currency || loc.currency}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-5">
          <Box title="Supplier" testid="po-print-supplier">
            <p className="font-bold text-[13px]">{po.supplier_name}</p>
            {po.supplier_code && <p className="text-slate-500">Supplier code: {po.supplier_code}</p>}
            {po._supplier && (() => {
              const s = po._supplier;
              const line2 = [s.suburb, s.state, s.postcode].filter(Boolean).join(' ');
              return (<>
                {s.contact_name && <p>Attn: {s.contact_name}</p>}
                {s.address && <p>{s.address}</p>}
                {line2 && <p>{line2}{s.country ? `, ${s.country}` : ''}</p>}
                {(s.phone || s.email) && <p className="text-slate-600">{[s.phone, s.email].filter(Boolean).join('  ·  ')}</p>}
              </>);
            })()}
          </Box>
          <Box title="Deliver to" testid="po-print-deliver">
            <p className="font-bold text-[13px]">{co.name}</p>
            <p className="whitespace-pre-wrap">{po.delivery_address || (co.addressLines || []).join(', ')}</p>
          </Box>
        </div>

        {po.delivery_instructions && (
          <div className="mt-4 border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-[12px]" data-testid="po-print-instructions">
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-800">Delivery instructions</p>
            <p className="whitespace-pre-wrap mt-0.5">{po.delivery_instructions}</p>
          </div>
        )}
        {!general && po.test_cert_required && <p className="mt-3 text-[12px] font-bold">Test certificates required with delivery.</p>}

        <table className="w-full mt-5 text-[12px] border-collapse" data-testid="po-print-lines">
          <thead>
            <tr className="bg-slate-900 text-white">
              <th className="px-2 py-2 text-left w-8">#</th>
              <th className="px-2 py-2 text-left">Description</th>
              {showDims && <th className="px-2 py-2 text-left" data-testid="po-print-dims-col">Dimensions</th>}
              <th className="px-2 py-2 text-right w-20">Qty</th>
              <th className="px-2 py-2 text-right w-24">Unit price{po.prices_include_tax && showTax ? ` (inc ${loc.tax_name})` : ''}</th>
              {showTax && <th className="px-2 py-2 text-right w-16">{loc.tax_name}</th>}
              <th className="px-2 py-2 text-right w-28">Amount</th>
            </tr>
          </thead>
          <tbody>
            {po.line_items.map((item, i) => (
              <tr key={item.id} className={`border-b border-slate-200 ${i % 2 ? 'bg-slate-50' : ''}`}>
                <td className="px-2 py-2 align-top text-slate-500">{i + 1}</td>
                <td className="px-2 py-2 align-top">
                  <p className="font-semibold">{lineTitle(item)}</p>
                  {lineSub(item) && <p className="text-[10.5px] text-slate-500">{lineSub(item)}</p>}
                  {item.is_backorder && <p className="text-[10.5px] font-bold text-amber-700">{backorderNote(item)} — reminder of goods still owed, not charged again</p>}
                </td>
                {showDims && <td className="px-2 py-2 align-top">{lineSize(item)}</td>}
                <td className="px-2 py-2 align-top text-right whitespace-nowrap">{item.quantity_ordered} {item.unit}</td>
                <td className="px-2 py-2 align-top text-right">{money(item.price_per_unit, po.currency)}</td>
                {showTax && <td className="px-2 py-2 align-top text-right">{Number(item.tax_rate) > 0 ? `${item.tax_rate}%` : '—'}</td>}
                <td className="px-2 py-2 align-top text-right font-semibold">{item.is_backorder ? '—' : money(item.line_total, po.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-between gap-6 mt-4">
          <div className="flex-1 text-[11.5px]">
            {po.notes && (<><p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Notes</p><p className="whitespace-pre-wrap mt-0.5">{po.notes}</p></>)}
          </div>
          <table className="text-[12.5px] w-64" data-testid="po-print-totals">
            <tbody>
              <tr><td className="py-1 text-slate-600">Subtotal{po.prices_include_tax ? ` (ex ${loc.tax_name})` : ''}</td><td className="py-1 text-right">{money(po.subtotal, po.currency)}</td></tr>
              <tr><td className="py-1 text-slate-600">{loc.tax_name}{po.prices_include_tax ? ' included' : ''}</td><td className="py-1 text-right">{money(po.tax, po.currency)}</td></tr>
              <tr className="border-t-2 border-slate-900"><td className="py-2 font-bold">Total {po.currency || loc.currency}</td><td className="py-2 text-right font-extrabold text-[16px]">{money(po.total, po.currency)}</td></tr>
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-2 gap-8 mt-10 text-[11px]">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Terms</p>
            <p>Please quote the PO number on all delivery dockets and invoices.</p>
            <p>Confirm receipt of this order and the delivery date. Advise immediately of any back orders or substitutions.</p>
          </div>
          <div className="self-end">
            <div className="border-b border-slate-900 h-10" />
            <p className="mt-1 text-slate-600">Authorised by{po.created_by ? ` — ${po.created_by}` : ''}</p>
          </div>
        </div>

        <p className="mt-8 pt-2 border-t border-slate-300 text-[10px] text-slate-400 text-center">
          {[co.name, co.abn && `ABN ${co.abn}`, co.phone, co.email].filter(Boolean).join('  ·  ')}
        </p>
      </div>
    </>
  );
};

export default PrintPO;
